/**
 * A weekly training plan, built by rules from what the person told us (B-46, D-99).
 *
 * Rules and not a model, for the reasons the spec gives: the same answers give the same
 * plan every time, a test can say what a plan for a bad knee must never contain, and
 * both clients can build one without a network. The model's part is narrower and comes
 * after — it reviews a plan against the free-text injury notes these rules cannot read
 * (`planReview.ts`), and only when there are any.
 *
 * What a plan deliberately does not hold is weights. It says what to do and how many
 * times; Today fills the weight from the last time the exercise was logged
 * (`todaySuggestion`), because a number made up here would be stored the moment a set is
 * ticked and would look exactly like a real one (D-78).
 *
 * Pure: no clock (`now` is passed in), no randomness, and nothing imported beyond core.
 */
import { exerciseById } from "./catalog.js";
import { JOINT_AREAS, hasRedFlag, recordedConditions } from "./onboarding.js";
import { STRAIN } from "./strain.js";
/** How many exercises fit a session of this length. */
export const EXERCISE_CAP = { 30: 4, 45: 5, 60: 6, 90: 8 };
const TEMPLATES = {
    "Full body A": ["quads", "chest", "back", "hamstrings", "shoulders", "core", "biceps", "triceps"],
    "Full body B": ["hamstrings", "back", "chest", "quads", "shoulders", "glutes", "triceps", "core"],
    Push: ["chest", "shoulders", "chest", "triceps", "shoulders", "triceps", "chest", "core"],
    Pull: ["back", "back", "biceps", "back", "shoulders", "biceps", "forearms", "core"],
    Legs: ["quads", "hamstrings", "glutes", "quads", "calves", "hamstrings", "core", "calves"],
    "Upper A": ["chest", "back", "shoulders", "back", "chest", "biceps", "triceps", "core"],
    "Upper B": ["back", "chest", "shoulders", "chest", "back", "triceps", "biceps", "core"],
    Upper: ["chest", "back", "shoulders", "back", "chest", "biceps", "triceps", "core"],
    "Lower A": ["quads", "hamstrings", "glutes", "calves", "quads", "core", "hamstrings", "core"],
    "Lower B": ["hamstrings", "quads", "glutes", "calves", "hamstrings", "core", "quads", "core"],
    Lower: ["quads", "hamstrings", "glutes", "calves", "quads", "core", "hamstrings", "core"],
};
/**
 * The exercises a plan chooses from, per group, best first: the staple compounds, then
 * the machine and cable work, then the rest. Deliberately not the whole catalog —
 * weighted and assisted variants, rack pulls and plate pinches are real exercises but
 * not ones a generated plan should hand to someone.
 */
const PREFERENCE = {
    chest: [
        "flat-barbell-bench-press",
        "incline-dumbbell-press",
        "flat-dumbbell-press",
        "machine-chest-press",
        "incline-barbell-bench-press",
        "push-up",
        "mid-cable-fly",
        "pec-deck",
        "incline-dumbbell-fly",
        "low-to-high-cable-fly",
        "high-to-low-cable-fly",
        "smith-machine-bench-press",
        "knee-push-up",
        "chest-dips",
    ],
    back: [
        "barbell-row",
        "wide-grip-lat-pulldown",
        "seated-cable-row",
        "pull-up",
        "chest-supported-dumbbell-row",
        "neutral-grip-lat-pulldown",
        "single-arm-dumbbell-row",
        "machine-low-row",
        "close-grip-lat-pulldown",
        "t-bar-row",
        "machine-high-row",
        "straight-arm-pulldown",
        "assisted-pull-up",
        "conventional-deadlift",
    ],
    shoulders: [
        "seated-dumbbell-shoulder-press",
        "lateral-side-raise",
        "face-pull",
        "machine-shoulder-press",
        "cable-lateral-raise",
        "reverse-pec-deck",
        "standing-barbell-overhead-press",
        "arnold-press",
        "cable-rear-delt-fly",
        "machine-lateral-raise",
        "landmine-press",
        "dumbbell-front-raise",
    ],
    biceps: [
        "ez-bar-curl",
        "hammer-curl",
        "incline-dumbbell-curl",
        "machine-preacher-curl",
        "rope-cable-curl",
        "standing-dumbbell-curl",
        "straight-bar-cable-curl",
        "concentration-curl",
    ],
    triceps: [
        "rope-tricep-pushdown",
        "dumbbell-overhead-tricep-extension",
        "ez-bar-skull-crusher",
        "v-bar-tricep-pushdown",
        "machine-tricep-extension",
        "cable-rope-overhead-tricep-extension",
        "close-grip-bench-press",
        "tricep-kickback",
    ],
    forearms: ["dumbbell-wrist-curl", "reverse-wrist-curl", "farmers-carry", "ez-bar-reverse-curl"],
    quads: [
        "back-squat",
        "leg-press",
        "goblet-squat",
        "leg-extension",
        "hack-squat",
        "bulgarian-split-squat",
        "walking-lunge",
        "smith-machine-squat",
        "reverse-lunge",
        "step-up",
        "pendulum-squat",
        "front-squat",
    ],
    hamstrings: [
        "barbell-romanian-deadlift",
        "lying-leg-curl",
        "seated-leg-curl",
        "dumbbell-romanian-deadlift",
        "glute-ham-raise",
        "stiff-leg-deadlift",
    ],
    glutes: [
        "barbell-hip-thrust",
        "machine-hip-thrust",
        "glute-bridge",
        "cable-pull-through",
        "hip-abduction-machine",
        "cable-kickback",
    ],
    calves: ["standing-machine-calf-raise", "seated-calf-raise", "leg-press-calf-raise", "smith-machine-calf-raise"],
    core: [
        "plank",
        "cable-crunch",
        "hanging-knee-raise",
        "dead-bug",
        "pallof-press",
        "side-plank",
        "ab-wheel-rollout",
        "lying-leg-raise",
        "machine-crunch",
        "cable-woodchop",
        "russian-twist",
        "hanging-leg-raise",
    ],
    // Only the timed conditioning work: `fullBody` is used for finishers, never a slot.
    fullBody: [
        "rowing-machine",
        "cycle",
        "elliptical",
        "ski-erg",
        "assault-bike",
        "battle-ropes",
        "treadmill",
        "sled-push",
    ],
};
/** Machine and cable work that is easier to learn and to do safely alone; tried first for someone new. */
const BEGINNER_FIRST = new Set([
    "machine-chest-press",
    "pec-deck",
    "mid-cable-fly",
    "wide-grip-lat-pulldown",
    "seated-cable-row",
    "machine-low-row",
    "chest-supported-dumbbell-row",
    "machine-shoulder-press",
    "cable-lateral-raise",
    "machine-lateral-raise",
    "reverse-pec-deck",
    "machine-preacher-curl",
    "rope-cable-curl",
    "rope-tricep-pushdown",
    "machine-tricep-extension",
    "leg-press",
    "leg-extension",
    "goblet-squat",
    "lying-leg-curl",
    "seated-leg-curl",
    "machine-hip-thrust",
    "glute-bridge",
    "seated-calf-raise",
    "leg-press-calf-raise",
    "plank",
    "dead-bug",
    "cable-crunch",
]);
const GROUP_WORDS = {
    chest: "chest",
    back: "back",
    shoulders: "shoulder",
    biceps: "biceps",
    triceps: "triceps",
    forearms: "forearm",
    quads: "quad",
    hamstrings: "hamstring",
    glutes: "glute",
    calves: "calf",
    core: "core",
    fullBody: "conditioning",
};
function schemeFor(input, redFlag) {
    const goal = input.goals[0];
    const base = goal === "strength"
        ? redFlag
            ? { sets: 3, reps: 8, seconds: 30 }
            : { sets: 4, reps: 5, seconds: 30 }
        : goal === "muscle"
            ? { sets: 3, reps: 10, seconds: 45 }
            : goal === "fatLoss" || goal === "general"
                ? { sets: 3, reps: 12, seconds: 60 }
                : goal === "endurance"
                    ? { sets: 3, reps: 15, seconds: 60 }
                    : goal === "mobility"
                        ? { sets: 2, reps: 12, seconds: 30 }
                        : { sets: 3, reps: 10, seconds: 45 };
    return input.prefs.experience === "new" ? { ...base, sets: Math.max(2, base.sets - 1) } : base;
}
function splitFor(prefs) {
    switch (prefs.days.length) {
        case 2:
            return ["Full body A", "Full body B"];
        case 3:
            return prefs.experience === "new" ? ["Full body A", "Full body B", "Full body A"] : ["Push", "Pull", "Legs"];
        case 4:
            return ["Upper A", "Lower A", "Upper B", "Lower B"];
        case 5:
            return ["Push", "Pull", "Legs", "Upper", "Lower"];
        default:
            return ["Push", "Pull", "Legs", "Push", "Pull", "Legs"];
    }
}
/**
 * The exercises this person may be given for a group, best first, with everything their
 * answers rule out already removed. Exported because the injury review may only swap to
 * one of these — a swap the rules themselves would have refused is not a swap (§5.3).
 */
export function candidatesFor(group, input) {
    const joints = new Set(recordedConditions(input.health)
        .map((c) => c.id)
        .filter((id) => JOINT_AREAS.includes(id)));
    const redFlag = hasRedFlag(input.health);
    const noImpact = (input.bodyType === "heavier" && input.goals.includes("fatLoss")) || joints.has("knee") || joints.has("ankleFoot");
    const lowOnly = input.goals[0] === "mobility";
    const allowed = PREFERENCE[group].filter((id) => {
        const strain = STRAIN[id];
        if (!strain)
            return false;
        if (strain.areas.some((a) => joints.has(a)))
            return false;
        if (redFlag && strain.intensity === "high")
            return false;
        if (noImpact && strain.impact)
            return false;
        if (lowOnly && strain.intensity !== "low")
            return false;
        return true;
    });
    if (input.prefs.experience !== "new")
        return allowed;
    return [...allowed.filter((id) => BEGINNER_FIRST.has(id)), ...allowed.filter((id) => !BEGINNER_FIRST.has(id))];
}
function assertBuildable(input) {
    if (input.goals.length === 0)
        throw new Error("buildPlan needs at least one goal");
    const days = input.prefs.days;
    if (days.length < 2 || days.length > 6)
        throw new Error("buildPlan needs two to six training days");
    if (new Set(days).size !== days.length)
        throw new Error("buildPlan was given a weekday twice");
    if (days.some((d) => !Number.isInteger(d) || d < 0 || d > 6))
        throw new Error("buildPlan was given a day that is not a weekday");
}
/** Monday first, Sunday last: a training week, not a calendar row. */
const mondayFirst = (day) => (day + 6) % 7;
export function buildPlan(input, now) {
    assertBuildable(input);
    const redFlag = hasRedFlag(input.health);
    const scheme = schemeFor(input, redFlag);
    const cap = EXERCISE_CAP[input.prefs.sessionMinutes];
    const finisher = input.goals[0] === "fatLoss" || input.goals[0] === "general";
    const weekdays = [...input.prefs.days].sort((a, b) => mondayFirst(a) - mondayFirst(b));
    const split = splitFor(input.prefs);
    const usedThisWeek = new Set();
    const days = {};
    weekdays.forEach((weekday, i) => {
        const label = split[i];
        const slots = TEMPLATES[label].slice(0, cap);
        if (finisher)
            slots[slots.length - 1] = "fullBody";
        const exercises = [];
        const gaps = [];
        const usedToday = new Set();
        for (const group of slots) {
            const candidates = candidatesFor(group, input);
            const pick = candidates.find((id) => !usedToday.has(id) && !usedThisWeek.has(id)) ??
                candidates.find((id) => !usedToday.has(id));
            if (pick === undefined) {
                if (!gaps.some((g) => g.muscleGroup === group)) {
                    gaps.push({ muscleGroup: group, reason: `No ${GROUP_WORDS[group]} exercise fits your answers` });
                }
                continue;
            }
            usedToday.add(pick);
            usedThisWeek.add(pick);
            const entry = exerciseById(pick);
            const timed = entry.measure === "duration";
            exercises.push({
                catalogId: pick,
                name: entry.name,
                muscleGroup: entry.muscleGroup,
                sets: group === "fullBody" ? Math.min(scheme.sets, 3) : scheme.sets,
                targetReps: group === "fullBody" ? 60 : timed ? scheme.seconds : scheme.reps,
            });
        }
        days[weekday] = { label, exercises, gaps };
    });
    return {
        days,
        generatedAt: now,
        builtFrom: input,
        review: recordedConditions(input.health).length > 0 ? "pending" : "notNeeded",
        removed: [],
        redFlag,
    };
}
const REVIEW_STATES = new Set(["notNeeded", "pending", "applied", "failed"]);
const isObject = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
/**
 * Whether a stored document is a plan this code can render.
 *
 * A shape check, not a validation of the choices in it: the document is the user's own
 * and was written by this app, so what is being guarded against is an older or
 * half-written shape, not a hostile one. A document that fails is treated as no plan,
 * and Profile says it could not be read.
 */
export function isPlan(value) {
    if (!isObject(value))
        return false;
    if (typeof value.generatedAt !== "number" || typeof value.redFlag !== "boolean")
        return false;
    if (typeof value.review !== "string" || !REVIEW_STATES.has(value.review))
        return false;
    if (!Array.isArray(value.removed) || !isObject(value.builtFrom) || !isObject(value.days))
        return false;
    const builtFrom = value.builtFrom;
    if (!Array.isArray(builtFrom.goals) || !isObject(builtFrom.health) || !isObject(builtFrom.prefs))
        return false;
    for (const [key, day] of Object.entries(value.days)) {
        if (!/^[0-6]$/.test(key) || !isObject(day))
            return false;
        if (typeof day.label !== "string" || !Array.isArray(day.exercises) || !Array.isArray(day.gaps))
            return false;
        for (const e of day.exercises) {
            if (!isObject(e) || typeof e.catalogId !== "string" || typeof e.name !== "string")
                return false;
            if (typeof e.sets !== "number" || typeof e.targetReps !== "number")
                return false;
        }
    }
    return true;
}
/** JSON with object keys sorted, so a document Firestore hands back reordered compares equal. */
function canonical(value) {
    if (Array.isArray(value))
        return `[${value.map(canonical).join(",")}]`;
    if (isObject(value)) {
        return `{${Object.keys(value)
            .sort()
            .filter((k) => value[k] !== undefined)
            .map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value);
}
/** True when any answer the plan was built from is different now, or gone. */
export function planIsStale(plan, profile) {
    const { bodyType, goals, health, planPrefs } = profile;
    if (bodyType === undefined || goals === undefined || health === undefined || planPrefs === undefined)
        return true;
    return canonical(plan.builtFrom) !== canonical({ bodyType, goals, health, prefs: planPrefs });
}
//# sourceMappingURL=plan.js.map